import type { UIMessage, UIMessagePart, UITools } from 'ai';
import z from 'zod';
export type AgentIds = 'agentFleetManager' | 'agentTrackingAnalyzer' | 'agentRecommendations' | 'agentGuidedTour';
export type AgentNames = 'agentFleetManager' | 'agent-tracking-analyzer' | 'agent-recommendations' | 'agent-guided-tour';

export type UserId = string;
export type FleetId = string;

export type Source = 'web' | 'mobile';
export type Component = 'full-page' | 'sidepanel' | 'floating' | 'sidepanel-tracking' | 'orders-ai-page';

/** A file the user attached to a message. `url` is a base64 `data:` URL; the model
 * reads the bytes as multimodal input. Mirrors AI SDK v6's `FileUIPart`. */
export type UserFileMessagePart = { type: 'file'; mediaType: string; url: string; filename?: string };

export type UserMessagePart = { type: 'text'; text: string } | UserFileMessagePart;

/**
 * Where a user-role message came from, so the AI API can tell prompts the user typed
 * (`user_input`) apart from prompts we authored (everything else). Sent to the backend in the
 * message metadata; the LLM never sees it (`convertToModelMessages` strips message metadata).
 * - `user_input` — typed in a chat composer
 * - `suggestion` — a suggestion card / welcome quick prompt
 * - `news` — an action from the Cobli IA news center
 * - `recommendation` — a recommendation CTA deep-link
 * - `feature_action` — a contextual "Analisar com a IA" button on another page
 * - `question_answer` — answers submitted from a `data-question` card (see `AIQuestionnaireCard`)
 */
export const MESSAGE_ORIGINS = ['user_input', 'suggestion', 'news', 'recommendation', 'feature_action', 'question_answer'] as const;
export type MessageOrigin = (typeof MESSAGE_ORIGINS)[number];

export type UserTextMessage = {
  role: 'user';
  id: string;
  parts: Array<UserMessagePart>;
  /** When `hidden` is true, the message drives an agent turn but is filtered from the rendered transcript. */
  metadata?: { hidden?: boolean; origin?: MessageOrigin; suggestionId?: string };
};

export type SendUserTextMessage = (message: UserTextMessage) => Promise<void> | void;

export const AGENTS = {
  agentFleetManager: 'agentFleetManager',
  agentTrackingAnalyzer: 'agent-tracking-analyzer',
  agentRecommendations: 'agent-recommendations',
  // The AI API only accepts `clientTools` for this id, never the registry key (`agentGuidedTour`).
  agentGuidedTour: 'agent-guided-tour',
} as const satisfies Record<AgentIds, AgentNames>;

type DefaultResourceId = `${UserId}_${FleetId}`;
type AssistantResourceId = `assistant_${UserId}_${FleetId}`;
type TrackingResourceId = `tracking_${string}`;

export type ResourceId = DefaultResourceId | AssistantResourceId | TrackingResourceId;

export const toolNameMap: Record<string, string> = {
  // ─────────────────────────────────────────────────────────────
  // Stream parts (não são tools)
  // ─────────────────────────────────────────────────────────────
  'step-start': 'Iniciando...',
  text: 'Processando...',
  reasoning: 'Pensando...',
  'tool-invocation': 'Executando ferramenta',

  // ─────────────────────────────────────────────────────────────
  // Meta-tools do runtime Mastra (ids fixos do @mastra/core)
  // ─────────────────────────────────────────────────────────────
  'tool-search_tools': 'Buscando ferramentas...',
  'tool-load_tool': 'Carregando ferramenta...',
  'tool-search_skills': 'Buscando habilidades...',
  'tool-load_skill': 'Carregando habilidade...',
  'tool-skill': 'Ativando habilidade',
  'tool-skill_search': 'Buscando habilidades...',
  'tool-skill_read': 'Consultando habilidade',
  'tool-updateWorkingMemory': 'Atualizando memória de trabalho',

  // Workspace tools (prefixo mastra_workspace_)
  'tool-mastra_workspace_read_file': 'Lendo arquivo',
  'tool-mastra_workspace_write_file': 'Escrevendo arquivo',
  'tool-mastra_workspace_edit_file': 'Editando arquivo',
  'tool-mastra_workspace_ast_edit': 'Editando arquivo',
  'tool-mastra_workspace_list_files': 'Listando arquivos',
  'tool-mastra_workspace_file_stat': 'Verificando arquivo',
  'tool-mastra_workspace_mkdir': 'Criando pasta',
  'tool-mastra_workspace_delete': 'Removendo arquivo',
  'tool-mastra_workspace_grep': 'Procurando no conteúdo',
  'tool-mastra_workspace_search': 'Procurando no workspace',
  'tool-mastra_workspace_index': 'Indexando workspace',
  'tool-mastra_workspace_execute_command': 'Executando comando',
  'tool-mastra_workspace_get_process_output': 'Lendo saída do processo',
  'tool-mastra_workspace_kill_process': 'Encerrando processo',
  'tool-mastra_workspace_lsp_inspect': 'Inspecionando código',

  // ─────────────────────────────────────────────────────────────
  // Delegação para sub-agents (agents: { key } → agent-<key>)
  // ─────────────────────────────────────────────────────────────
  'tool-agent-files': 'Preparando arquivo',
  'tool-agent-scheduling': 'Consultando agendamentos',

  // Workflows expostos como tool (workflows: { key } → workflow-<key>)
  'tool-workflow-trackingAnalyzerWorkflow': 'Analisando trajeto',

  // ─────────────────────────────────────────────────────────────
  // Domain: Files / Sandbox
  // ─────────────────────────────────────────────────────────────
  'tool-fileOperationsTool': 'Operações com arquivo(s)',
  'tool-file-operations': 'Operações com arquivo(s)',
  'tool-executeCodeTool': 'Executando análise',
  'tool-execute-code': 'Executando análise',
  'tool-executeCommandTool': 'Executando operação',
  'tool-execute-command': 'Executando operação',
  'tool-uploadFileTool': 'Disponibilizando arquivo',
  'tool-upload-file-to-s3': 'Disponibilizando arquivo',
  'tool-companyLogoToSandboxTool': 'Aplicando identidade visual',
  'tool-company-logo-to-sandbox': 'Aplicando identidade visual',

  // ─────────────────────────────────────────────────────────────
  // Domain: Charts & KPIs
  // ─────────────────────────────────────────────────────────────
  'tool-chartsDataTool': 'Calculando dados para gráficos',
  'tool-charts-data-tool': 'Calculando dados para gráficos',
  'tool-kpiDataTool': 'Calculando indicadores',
  'tool-kpi-data-tool': 'Calculando indicadores',

  // ─────────────────────────────────────────────────────────────
  // Domain: Knowledge base & perfil da frota
  // ─────────────────────────────────────────────────────────────
  'tool-knowledgeBaseTool': 'Buscando dados gerais',
  'tool-knowledge-base-tool': 'Buscando dados gerais',
  'tool-fleetProfileTool': 'Buscando perfil da frota',
  'tool-fleet-profile-tool': 'Buscando perfil da frota',

  // ─────────────────────────────────────────────────────────────
  // Domain: Vehicles
  // ─────────────────────────────────────────────────────────────
  'tool-vehiclesListTool': 'Construindo lista de veículos',
  'tool-vehicles-list-tool': 'Construindo lista de veículos',
  'tool-vehicleDetailsTool': 'Buscando detalhes do veículo',
  'tool-vehicle-details-tool': 'Buscando detalhes do veículo',
  'tool-vehiclesOverviewTool': 'Buscando dados sobre veículos',
  'tool-vehicle-overview-tool': 'Buscando dados sobre veículos',
  'tool-createVehicleTool': 'Cadastrando veículo',
  'tool-create-vehicle-tool': 'Cadastrando veículo',
  'tool-createVehicleGroupTool': 'Criando grupo de veículos',
  'tool-create-vehicle-group-tool': 'Criando grupo de veículos',
  'tool-deleteVehiclesTool': 'Removendo veículo(s)',
  'tool-delete-vehicles-tool': 'Removendo veículo(s)',
  'tool-assignDriverToVehicleTool': 'Associando motorista ao veículo',
  'tool-assign-driver-to-vehicle-tool': 'Associando motorista ao veículo',
  'tool-associateVehicleToDeviceTool': 'Associando dispositivo ao veículo',
  'tool-associate-vehicle-to-device-tool': 'Associando dispositivo ao veículo',
  'tool-vehicleAssociationHistoryByDriverTool': 'Buscando histórico de veículos do motorista',
  'tool-vehicle-association-history-by-driver': 'Buscando histórico de veículos do motorista',
  'tool-driverVehicleAssociationHistoryTool': 'Buscando histórico de motoristas do veículo',
  'tool-driver-vehicle-association-history': 'Buscando histórico de motoristas do veículo',
  'tool-vehiclesAliasSuggestionTool': 'Sugerindo apelidos de veículos',
  'tool-vehicles-alias-suggestion-tool': 'Sugerindo apelidos de veículos',

  // ─────────────────────────────────────────────────────────────
  // Domain: Drivers
  // ─────────────────────────────────────────────────────────────
  'tool-driversListTool': 'Buscando dados sobre motoristas',
  'tool-drivers-list-tool': 'Buscando dados sobre motoristas',
  'tool-driverDetailsTool': 'Buscando detalhes do motorista',
  'tool-driver-details-tool': 'Buscando detalhes do motorista',
  'tool-driversOverviewTool': 'Buscando dados sobre motoristas',
  'tool-driver-overview-tool': 'Buscando dados sobre motoristas',
  'tool-driversLicenseExpirationTool': 'Buscando dados sobre CNH',
  'tool-drivers-license-expiration-tool': 'Buscando dados sobre CNH',
  'tool-driversAddTool': 'Adicionando motorista',
  'tool-drivers-add-tool': 'Adicionando motorista',

  // ─────────────────────────────────────────────────────────────
  // Domain: Groups
  // ─────────────────────────────────────────────────────────────
  'tool-groupsTool': 'Buscando dados sobre grupos de veículos',
  'tool-groups-tool': 'Buscando dados sobre grupos de veículos',

  // ─────────────────────────────────────────────────────────────
  // Domain: Users
  // ─────────────────────────────────────────────────────────────
  'tool-inviteUserTool': 'Convidando usuário',
  'tool-invite-user-tool': 'Convidando usuário',

  // ─────────────────────────────────────────────────────────────
  // Domain: Checklists
  // ─────────────────────────────────────────────────────────────
  'tool-checklistsListTool': 'Buscando checklists',
  'tool-checklists-list-tool': 'Buscando checklists',
  'tool-createChecklistTool': 'Criando checklist',
  'tool-create-checklist-tool': 'Criando checklist',

  // ─────────────────────────────────────────────────────────────
  // Domain: Places / Geofences
  // ─────────────────────────────────────────────────────────────
  'tool-createPlaceTool': 'Criando local de interesse',
  'tool-create-place-tool': 'Criando local de interesse',
  'tool-listGeofencesTool': 'Buscando locais de interesse',
  'tool-list-geofences-tool': 'Buscando locais de interesse',
  'tool-getGeofenceTool': 'Buscando detalhes do local',
  'tool-get-geofence-tool': 'Buscando detalhes do local',
  'tool-deleteGeofenceTool': 'Removendo local de interesse',
  'tool-delete-geofence-tool': 'Removendo local de interesse',

  // ─────────────────────────────────────────────────────────────
  // Domain: Alerts
  // ─────────────────────────────────────────────────────────────
  'tool-listFleetAlertTypesTool': 'Buscando tipos de alerta',
  'tool-list-fleet-alert-types-tool': 'Buscando tipos de alerta',
  'tool-createFleetAlertTool': 'Criando alerta',
  'tool-create-fleet-alert-tool': 'Criando alerta',
  'tool-listFleetAlertsTool': 'Buscando alertas da frota',
  'tool-list-fleet-alerts-tool': 'Buscando alertas da frota',
  'tool-setFleetAlertActiveTool': 'Atualizando status do alerta',
  'tool-set-fleet-alert-active-tool': 'Atualizando status do alerta',

  // ─────────────────────────────────────────────────────────────
  // Domain: Devices
  // ─────────────────────────────────────────────────────────────
  'tool-devicesOverviewTool': 'Buscando dados sobre dispositivos',
  'tool-devices-overview-tool': 'Buscando dados sobre dispositivos',
  'tool-devicesListTool': 'Construindo lista de dispositivos',
  'tool-devices-list-tool': 'Construindo lista de dispositivos',
  'tool-setDeviceSpareStatusTool': 'Atualizando dispositivo reserva',
  'tool-set-device-spare-status-tool': 'Atualizando dispositivo reserva',

  // ─────────────────────────────────────────────────────────────
  // Domain: Cameras
  // ─────────────────────────────────────────────────────────────
  'tool-createWifiConfigTool': 'Criando rede Wi-Fi',
  'tool-create-wifi-config-tool': 'Criando rede Wi-Fi',
  'tool-editWifiConfigTool': 'Editando rede Wi-Fi',
  'tool-edit-wifi-config-tool': 'Editando rede Wi-Fi',
  'tool-deleteWifiConfigTool': 'Removendo rede Wi-Fi',
  'tool-delete-wifi-config-tool': 'Removendo rede Wi-Fi',
  'tool-listWifiConfigsTool': 'Buscando redes Wi-Fi',
  'tool-list-wifi-configs-tool': 'Buscando redes Wi-Fi',
  'tool-listCameraConfigurationsTool': 'Buscando configurações de câmera',
  'tool-list-camera-configurations-tool': 'Buscando configurações de câmera',
  'tool-getFleetEventsConfigTool': 'Buscando configuração de eventos',
  'tool-get-fleet-events-config-tool': 'Buscando configuração de eventos',
  'tool-updateFleetEventSensibilityTool': 'Ajustando sensibilidade de eventos',
  'tool-update-fleet-event-sensibility-tool': 'Ajustando sensibilidade de eventos',
  'tool-updateCameraAudioTool': 'Ajustando áudio da câmera',
  'tool-update-camera-audio-tool': 'Ajustando áudio da câmera',
  'tool-updateCameraSpeedLimitTool': 'Ajustando limite de velocidade da câmera',
  'tool-update-camera-speed-limit-tool': 'Ajustando limite de velocidade da câmera',

  // ─────────────────────────────────────────────────────────────
  // Domain: Maintenance
  // ─────────────────────────────────────────────────────────────
  'tool-maintenanceOverviewTool': 'Buscando dados sobre manutenções',
  'tool-maintenance_overview': 'Buscando dados sobre manutenções',
  'tool-maintenanceHistoryTool': 'Buscando dados sobre histórico de manutenções',
  'tool-maintenance-history': 'Buscando dados sobre histórico de manutenções',
  'tool-maintenanceRemindersTool': 'Buscando dados sobre lembretes de manutenção',
  'tool-maintenance-reminders': 'Buscando dados sobre lembretes de manutenção',
  'tool-maintenanceDrilldownTool': 'Buscando detalhamento de manutenções',
  'tool-maintenance-drilldown': 'Buscando detalhamento de manutenções',
  'tool-manutencaoDrilldownTool': 'Buscando detalhamento de manutenções',
  'tool-manutencao_drilldown': 'Buscando detalhamento de manutenções',

  // ─────────────────────────────────────────────────────────────
  // Domain: Productivity
  // ─────────────────────────────────────────────────────────────
  'tool-productivityOverviewTool': 'Buscando informações gerais de produtividade',
  'tool-productivity-overview': 'Buscando informações gerais de produtividade',
  'tool-productivityEntitiesQueryTool': 'Buscando dados sobre entidades de produtividade',
  'tool-productivity-entities-query': 'Buscando dados sobre entidades de produtividade',
  'tool-productivityEntityExplanationTool': 'Buscando dados sobre explicação de entidades de produtividade',
  'tool-productivity-entity-explanation': 'Buscando dados sobre explicação de entidades de produtividade',

  // ─────────────────────────────────────────────────────────────
  // Domain: Safety
  // ─────────────────────────────────────────────────────────────
  'tool-safetyOverviewTool': 'Buscando dados gerais sobre segurança',
  'tool-safety-overview': 'Buscando dados gerais sobre segurança',
  'tool-safetyRankingTool': 'Buscando dados sobre ranking de segurança',
  'tool-safety-ranking': 'Buscando dados sobre ranking de segurança',
  'tool-safetyEntitiesQueryTool': 'Buscando dados sobre entidades de segurança',
  'tool-safety-entities-query': 'Buscando dados sobre entidades de segurança',
  'tool-safetyEntityExplanationTool': 'Buscando dados sobre explicação de entidades de segurança',
  'tool-safety-entity-explanation': 'Buscando dados sobre explicação de entidades de segurança',
  'tool-safetyMaxSpeedPeakTool': 'Buscando dados sobre picos de velocidade',
  'tool-safety-max-speed-peak': 'Buscando dados sobre picos de velocidade',
  'tool-safetyTimeSeriesTool': 'Montando série histórica de segurança',
  'tool-safety-time-series': 'Montando série histórica de segurança',

  // ─────────────────────────────────────────────────────────────
  // Domain: Tracking
  // ─────────────────────────────────────────────────────────────
  'tool-trackingFetchDataTool': 'Buscando dados sobre trajeto',
  'tool-tracking-fetch-data-tool': 'Buscando dados sobre trajeto',
  'tool-trackingAnalyzeEventsTool': 'Analisando eventos do trajeto',
  'tool-tracking-analyze-events-tool': 'Analisando eventos do trajeto',
  'tool-trackingSpeedPathTool': 'Buscando velocidade ao longo do trajeto',
  'tool-tracking-speed-path': 'Buscando velocidade ao longo do trajeto',
  'tool-trackingReverseGeocodeTool': 'Convertendo coordenadas em endereço',
  'tool-tracking-reverse-geocode': 'Convertendo coordenadas em endereço',
  'tool-trackingTripsAndStopsDailyTool': 'Buscando viagens e paradas por dia',
  'tool-tracking-trips-and-stops-daily': 'Buscando viagens e paradas por dia',
  'tool-trackingTripsAndStopsAggregationTool': 'Agregando viagens e paradas',
  'tool-tracking-trips-and-stops-aggregation': 'Agregando viagens e paradas',
  'tool-trackingVehiclePlacesTool': 'Buscando locais visitados pelo veículo',
  'tool-tracking-vehicle-places': 'Buscando locais visitados pelo veículo',
  'tool-trackingVehicleHeatmapTool': 'Montando mapa de calor do veículo',
  'tool-tracking-vehicle-heatmap': 'Montando mapa de calor do veículo',
  'tool-trackingVehiclesInAreaTool': 'Buscando veículos na área',
  'tool-tracking-vehicles-in-area': 'Buscando veículos na área',
  'tool-trackingPassageHistoryTool': 'Buscando histórico de passagens',
  'tool-tracking-passage-history': 'Buscando histórico de passagens',

  // ─────────────────────────────────────────────────────────────
  // Domain: Geocode
  // ─────────────────────────────────────────────────────────────
  'tool-geocodeAddressTool': 'Localizando endereço',
  'tool-geocode-address': 'Localizando endereço',
  'tool-vehiclesNearTool': 'Buscando veículos próximos',
  'tool-vehicles-near': 'Buscando veículos próximos',

  // ─────────────────────────────────────────────────────────────
  // Domain: Fuel
  // ─────────────────────────────────────────────────────────────
  'tool-fuelOverviewTool': 'Buscando dados sobre combustível',
  'tool-combustivel_overview_frota': 'Buscando dados sobre combustível',
  'tool-fuelDrilldownTool': 'Buscando detalhamento de combustível',
  'tool-combustivel_drilldown': 'Buscando detalhamento de combustível',

  // ─────────────────────────────────────────────────────────────
  // Domain: Videos
  // ─────────────────────────────────────────────────────────────
  'tool-videoDailyRecommendationsTool': 'Buscando vídeos recomendados',
  'tool-daily-video-recommendations': 'Buscando vídeos recomendados',

  // ─────────────────────────────────────────────────────────────
  // Domain: Scheduling / Uninstall / Shipping (agent-scheduling)
  // ─────────────────────────────────────────────────────────────
  'tool-getFleetAddressTool': 'Buscando endereço da frota',
  'tool-get-fleet-address-tool': 'Buscando endereço da frota',
  'tool-schedulingListTicketsTool': 'Buscando agendamentos',
  'tool-scheduling-list-tickets-tool': 'Buscando agendamentos',
  'tool-schedulingListDevicesTool': 'Buscando dispositivos do agendamento',
  'tool-scheduling-list-devices-tool': 'Buscando dispositivos do agendamento',
  'tool-schedulingDevicesQuantityTool': 'Verificando quantidade de dispositivos',
  'tool-scheduling-devices-quantity-tool': 'Verificando quantidade de dispositivos',
  'tool-schedulingConfirmTool': 'Confirmando agendamento',
  'tool-scheduling-confirm-tool': 'Confirmando agendamento',
  'tool-schedulingConfirmSplitTool': 'Confirmando agendamento dividido',
  'tool-scheduling-confirm-split-tool': 'Confirmando agendamento dividido',
  'tool-schedulingTrackStepTool': 'Registrando etapa do agendamento',
  'tool-scheduling-track-step-tool': 'Registrando etapa do agendamento',
  'tool-uninstallListTicketsTool': 'Buscando desinstalações',
  'tool-uninstall-list-tickets-tool': 'Buscando desinstalações',
  'tool-uninstallDevicesQuantityTool': 'Verificando dispositivos para desinstalar',
  'tool-uninstall-devices-quantity-tool': 'Verificando dispositivos para desinstalar',
  'tool-uninstallConfirmTool': 'Confirmando desinstalação',
  'tool-uninstall-confirm-tool': 'Confirmando desinstalação',
  'tool-shippingListTicketsTool': 'Buscando envios',
  'tool-shipping-list-tickets-tool': 'Buscando envios',

  // ─────────────────────────────────────────────────────────────
  // Domain: Research / Onboarding (agent-fleet-onboarding)
  // ─────────────────────────────────────────────────────────────
  'tool-webSearch': 'Pesquisando na web',
  'tool-companyBrandingTool': 'Buscando identidade visual da empresa',
  'tool-company-branding-tool': 'Buscando identidade visual da empresa',
  'tool-companyDeepSearchTool': 'Pesquisando sobre a empresa',
  'tool-company-deep-search-tool': 'Pesquisando sobre a empresa',

  // ─────────────────────────────────────────────────────────────
  // Domain: Image generation (WhatsApp)
  // ─────────────────────────────────────────────────────────────
  'tool-generateAndStoreWhatsappImageTool': 'Gerando imagem',
  'tool-generate-and-store-whatsapp-image': 'Gerando imagem',

  // ─────────────────────────────────────────────────────────────
  // Legacy / nomes antigos — mantidos para threads históricas
  // ─────────────────────────────────────────────────────────────
  'tool-skill-activate': 'Ativando habilidade',
  'tool-code_execution': 'Executando análise',
  'tool-uploadS3Tool': 'Salvando arquivo',
  'tool-vehicleTool': 'Buscando dados sobre veículo',
  'tool-vehicleOverviewTool': 'Buscando dados sobre veículo',
  'tool-vehicleGroupsTool': 'Buscando dados sobre grupos de veículos',
} as const;

/**
 * Nome amigável das skills. O stream entrega `tool-skill` / `tool-skill_search`
 * com o nome da skill no input — use este mapa para rotular a linha.
 */
export const skillNameMap: Record<string, string> = {
  // agent-fleet-manager — arquétipos (perfil de uso da frota)
  'arquetipo-administrador-de-frota': 'Perfil: administrador de frota',
  'arquetipo-analista': 'Perfil: analista',
  'arquetipo-gestor-financeiro': 'Perfil: gestor financeiro',
  'arquetipo-gestor-generalista': 'Perfil: gestor generalista',
  'arquetipo-gestor-operacional': 'Perfil: gestor operacional',
  'arquetipo-gestor-seguranca': 'Perfil: gestor de segurança',
  'arquetipo-monitoramento-geral': 'Perfil: monitoramento geral',
  'arquetipo-planejador-de-rotas': 'Perfil: planejador de rotas',
  'arquetipo-rastreador-em-campo': 'Perfil: rastreador em campo',
  'arquetipo-supervisor-de-frota': 'Perfil: supervisor de frota',
  'arquetipo-uso-baixo': 'Perfil: uso baixo',

  // agent-fleet-manager — análises
  'fleet-analyzer': 'Analisando a frota',
  'fleet-device-analyzer': 'Analisando dispositivos',
  'fleet-maintenance-costs': 'Analisando custos de manutenção',
  'fleet-productivity-analyzer': 'Analisando produtividade',
  'fleet-safety-analyzer': 'Analisando segurança',
  'fuel-costs': 'Analisando custos de combustível',

  // agent-fleet-manager — ações
  'add-driver': 'Adicionando motorista',
  'assign-driver-to-vehicle': 'Associando motorista ao veículo',
  'associate-vehicle-device': 'Associando dispositivo ao veículo',
  'create-fleet-alert': 'Criando alerta',
  'create-geofence': 'Criando local de interesse',
  'create-vehicle': 'Cadastrando veículo',
  'create-vehicle-group': 'Criando grupo de veículos',
  'create-wifi-config': 'Criando rede Wi-Fi',
  'delete-vehicle': 'Removendo veículo',
  'driver-vehicle-association-history': 'Consultando histórico de associações',
  'invite-user': 'Convidando usuário',
  'list-camera-configurations': 'Consultando configurações de câmera',
  'manage-checklists': 'Gerenciando checklists',
  'manage-wifi-configs': 'Gerenciando redes Wi-Fi',
  'set-device-spare-status': 'Atualizando dispositivo reserva',
  'update-camera-audio': 'Ajustando áudio da câmera',
  'update-camera-speed-limit': 'Ajustando limite de velocidade',
  'update-fleet-event-sensibility': 'Ajustando sensibilidade de eventos',
  'cobli-ai-voice-tone': 'Ajustando tom de voz',

  // agent-files
  branding: 'Consultando identidade visual',
  'create-branded-business-files': 'Preparando arquivo com identidade visual',
  'docx-templates': 'Montando documento Word',
  pdf: 'Montando PDF',
  xlsx: 'Montando planilha',

  // agent-scheduling
  scheduling: 'Agendando instalação',
  'shipping-tracking': 'Acompanhando envio',
  'uninstall-scheduling': 'Agendando desinstalação',
};

/**
 * Represents tripwire data that can be included in AI messages.
 * Tripwires are warnings or safety checks that can interrupt or modify AI responses.
 */
const tripwireSchema = z.object({
  tripwireReason: z.string(),
});

export type TripwireData = z.infer<typeof tripwireSchema>;

const fileS3Schema = z.object({
  success: z.boolean(),
  status: z.enum(['loading', 'success', 'error']),
  data: z.array(
    z.object({
      toolCallId: z.string(),
      s3Path: z.string(),
      size: z.number(),
      filename: z.string(),
      s3Url: z.string(),
      contentType: z.string(),
      threadId: z.string().optional(),
    })
  ),
});

export type FileS3Data = z.infer<typeof fileS3Schema>;

const aiFileSchema = z.object({
  filePath: z.string(),
  filename: z.string(),
  threadId: z.string().optional(),
  threadTitle: z.string().optional(),
  url: z.string().optional(),
  size: z.number(),
  createdAt: z.string(),
  sessionId: z.string().optional(),
  userId: z.string().optional(),
  fleetId: z.string().optional(),
  contentType: z.string().optional(),
});

const aiFilesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(aiFileSchema),
});

export type AIFile = z.infer<typeof aiFileSchema>;
export type AIFilesResponse = z.infer<typeof aiFilesResponseSchema>;

/** Backend response after uploading a user attachment to S3. `url` is the (presigned/public)
 * S3 URL that goes into the outgoing message `file` part in place of a base64 data URL. */
const aiUploadedFileSchema = z.object({
  url: z.string(),
  filename: z.string(),
  contentType: z.string(),
  s3Path: z.string().optional(),
  size: z.number().optional(),
  threadId: z.string().optional(),
});

export type AIUploadedFile = z.infer<typeof aiUploadedFileSchema>;

const videoDataSchema = z.object({
  id: z.string(),
  panelUrl: z.string(),
  thumbnailUrl: z.string().optional(),
  driverName: z.string().optional(),
  severity: z.string().optional(),
  eventTrigger: z.string().optional(),
  timestamp: z.number().optional(),
});

export type VideoData = z.infer<typeof videoDataSchema>;

/**
 * One tool call of a subagent, as buffered by `@mastra/ai-sdk` inside the `data-tool-agent` part.
 * Entries are raw Mastra chunk payloads and the lib is inconsistent about the nesting — the same
 * array is written flat (`{ toolName }`) and read back as `{ payload: { toolName } }` — so both
 * shapes are typed and both are read.
 */
const agentSubToolCallArgsSchema = z.object({
  toolCallId: z.string().optional(),
  toolName: z.string().optional(),
  /** Settled call: the arguments as an object. `skill` uses `name`, `skill_read` uses `skillName`. */
  args: z.record(z.unknown()).optional(),
  input: z.record(z.unknown()).optional(),
  /** Streaming call: the raw JSON arguments, still partial until the input finishes. */
  argsText: z.string().optional(),
});

const agentSubToolCallSchema = agentSubToolCallArgsSchema.extend({
  payload: agentSubToolCallArgsSchema.optional(),
});

export type AgentSubToolCall = z.infer<typeof agentSubToolCallSchema>;

/**
 * A subagent dispatch streamed as a `data-tool-agent` part. The backend reconciles it in place
 * by the part `id`, updating `status` as the subagent runs. `data.id` is the subagent id
 * (e.g. `agent-scheduling`), mapped to a human label via the `agent_names` i18n object.
 *
 * `pendingToolCalls` / `toolCalls` / `toolResults` are the subagent's live tool activity, which is
 * what turns the card's generic "working" badge into the actual stage (see
 * `getRunningSubAgentTool`). They are reset at every step boundary by the transform, so a gap
 * between steps legitimately has no current tool.
 *
 * Only the fields the UI reads are typed; the stream carries more (usage, steps, text…).
 */
const agentToolDataSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  finishReason: z.string().nullish(),
  /** Tool calls whose input is still streaming — the earliest signal of the current stage. */
  pendingToolCalls: z.array(agentSubToolCallSchema).optional(),
  toolCalls: z.array(agentSubToolCallSchema).optional(),
  toolResults: z.array(agentSubToolCallSchema).optional(),
});

export type AgentToolData = z.infer<typeof agentToolDataSchema>;

/**
 * A multiple-choice question card streamed as a `data-question` part by the backend
 * `ask-user-question-tool`. The user answers by clicking — every question also has a free-text
 * field, rendered unconditionally by the card — and the answers go back as a hidden user
 * message: there is no tool-result round-trip (the transport only sends the last message, so
 * history lives in Mastra memory).
 */
const questionChoiceSchema = z.object({
  /** Technical value the target tool expects (e.g. `administrator`). Never shown to the user. */
  value: z.string(),
  label: z.string(),
  description: z.string().optional(),
  recommended: z.boolean().optional(),
});

const questionItemSchema = z.object({
  /** Stable key, matching the target tool's field (e.g. `role`). Used as the answer's fallback label. */
  name: z.string(),
  prompt: z.string(),
  header: z.string().optional(),
  multiple: z.boolean().optional(),
  required: z.boolean().optional(),
  choices: z.array(questionChoiceSchema),
});

const questionDataSchema = z.object({
  intro: z.string().optional(),
  questions: z.array(questionItemSchema),
});

export type QuestionChoice = z.infer<typeof questionChoiceSchema>;
export type QuestionItem = z.infer<typeof questionItemSchema>;
export type QuestionData = z.infer<typeof questionDataSchema>;

const PLAN_ITEM_STATUSES = ['pending', 'in_progress', 'completed'] as const;

const planItemSchema = z.object({
  id: z.string(),
  title: z.string().min(1),
  status: z.enum(PLAN_ITEM_STATUSES),
});

/**
 * The assistant's visible plan, streamed as a `data-plan` part (`id: plan-${runId}`). Every update
 * carries the whole plan, so the latest part replaces the previous ones. Only the shape is checked
 * here: the 1–8 items / ≤80 chars / single `in_progress` limits are the backend's contract, and a
 * plan that breaks them still renders.
 */
export const planDataSchema = z.object({
  items: z.array(planItemSchema).min(1),
});

export type PlanItemStatus = (typeof PLAN_ITEM_STATUSES)[number];
export type PlanItem = z.infer<typeof planItemSchema>;
export type PlanData = z.infer<typeof planDataSchema>;

/**
 * A tool call the agent paused on, waiting for the user to allow or refuse it
 * (`data-tool-call-approval`). Mastra emits this part next to the AI SDK-native
 * `tool-approval-request` chunk: the native chunk only carries the composite approval id
 * (`${runId}::${toolCallId}`), while this part carries what the UI needs to describe the pending
 * action — tool name and arguments. The decision goes back through `useChat`'s
 * `addToolApprovalResponse`, and `sendAutomaticallyWhen` resubmits the turn so the run resumes.
 */
const toolCallApprovalSchema = z.object({
  state: z.literal('data-tool-call-approval').optional(),
  /** Mastra run id — first half of the approval id the AI SDK matches the response against. */
  runId: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.unknown()).optional(),
  /** JSON schema of the resume payload. Unused by the confirm/deny card; kept for richer prompts. */
  resumeSchema: z.unknown().optional(),
});

export type ToolCallApprovalData = z.infer<typeof toolCallApprovalSchema>;

/**
 * Metadata that can be attached to UI messages.
 */
const metadataSchema = z.object({
  threadId: z.string().optional(),
  traceId: z.string().optional(),
  resourceId: z.string().optional(),
  createdAt: z.string().datetime().optional(),
  inputTokens: z.number().optional(),
  outputTokens: z.number().optional(),
  reasoningTokens: z.number().optional(),
  cachedInputTokens: z.number().optional(),
  maxTokens: z.number().optional(),
  modelId: z.string().optional(),
  /**
   * Marks a message as hidden from the rendered transcript (e.g. prompts triggered by a
   * button click). Model-invisible — `convertToModelMessages` strips message metadata, so
   * the agent only sees the prompt text. Persisted with the message and restored on reload,
   * so the message stays hidden across sessions. Replaces the legacy `[[AI_HIDDEN_PROMPT]]`
   * text marker.
   */
  hidden: z.boolean().optional(),
  /**
   * Origin of a user-role message (typed vs authored by us). Sent to the AI API so it can
   * distinguish real user input from suggestion/news/recommendation prompts. See `MessageOrigin`.
   */
  origin: z.enum(MESSAGE_ORIGINS).optional(),
});

type CobliChatMetadata = z.infer<typeof metadataSchema>;

/**
 * Schema for data parts in CobliChatUIMessage.
 * This schema is used to validate the data parts in the CobliChatUIMessage.
 */
const dataPartSchema = z.object({
  tripwire: tripwireSchema.optional(),
  file: fileS3Schema.optional(),
  // Required: a `data-video` part without its payload is meaningless, and consumers dereference `part.data` directly.
  video: videoDataSchema,
  // `data-tool-agent`: subagent dispatch feedback card. Required for the same reason as `video`.
  'tool-agent': agentToolDataSchema,
  // `data-question`: multiple-choice card. Required for the same reason as `video`.
  question: questionDataSchema,
  // `data-plan`: the assistant's visible plan. Required for the same reason as `video`.
  plan: planDataSchema,
  // `data-tool-call-approval`: pending human-in-the-loop confirmation. Required — the card is built
  // entirely from this payload.
  'tool-call-approval': toolCallApprovalSchema,
});

export type CobliChatDataPart = z.infer<typeof dataPartSchema>;

// https://ai-sdk.dev/docs/reference/ai-sdk-core/ui-message#creating-your-own-uimessage-type
export type CobliChatUIMessage = UIMessage<CobliChatMetadata, CobliChatDataPart, UITools>;

export type CobliChatUIMessagePart = UIMessagePart<CobliChatDataPart, UITools>;

export type DataVideoPart = {
  type: 'data-video';
  id: string;
  data: VideoData;
};

export type DataToolAgentPart = {
  type: 'data-tool-agent';
  id: string;
  data: AgentToolData;
};

export type DataQuestionPart = {
  type: 'data-question';
  id: string;
  data: QuestionData;
};

export type DataPlanPart = {
  type: 'data-plan';
  id?: string;
  data: PlanData;
};

/** Who a notification was addressed to: the signed-in user, or everyone in their fleet. */
export type AINotificationScope = 'user' | 'fleet';

/** Optional call to action on a notification. Absolute `http(s)` URLs open in a new tab; anything
 * else is treated as an in-app route. */
export type AINotificationLink = {
  url: string;
  /** Button copy. Falls back to a generic "open" label when the backend leaves it out. */
  label?: string;
};

/** A message authored for customers and surfaced by the assistant's notification bell. */
export type AINotification = {
  id: string;
  title: string;
  /** Plain text — the card clamps it to three lines. */
  text: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  /** Already resolved for the user behind the bearer token, fleet-scoped items included. */
  read: boolean;
  scope: AINotificationScope;
  link?: AINotificationLink;
};

export type AINotificationsResponse = {
  items: AINotification[];
};
